import { useState, useEffect, useCallback } from 'react';
import { useTenant } from '../context/TenantContext';

// SMTP-Konfiguration pro Mandant. Passwort wird verschlüsselt gespeichert
// (siehe server/utils/crypto.js). Beim Laden bekommt das Frontend nur einen
// gemaskten Hint zurück (z.B. ••••••XYZ4) — der echte Wert verlässt den Server
// nie. Beim Speichern bleibt das Passwortfeld leer = unverändert.
export default function EmailConfigPanel({ canEdit }) {
  const { activeTenant, tenantFetch } = useTenant();
  const [cfg, setCfg] = useState({
    host: '', port: 587, secure: false, user: '',
    fromAddress: '', fromName: '',
    passwordSet: false, passwordHint: ''
  });
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  // Test-Mail
  const [testTo, setTestTo] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState('');

  const load = useCallback(async () => {
    if (!activeTenant) return;
    setLoading(true);
    try {
      const res = await tenantFetch(`/api/tenants/${activeTenant._id}/email-config`);
      const data = await res.json();
      if (res.ok && data.emailConfig) setCfg(data.emailConfig);
    } finally {
      setLoading(false);
    }
  }, [activeTenant, tenantFetch]);

  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e?.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const body = {
        host: cfg.host,
        port: Number(cfg.port) || 587,
        secure: !!cfg.secure,
        user: cfg.user,
        fromAddress: cfg.fromAddress,
        fromName: cfg.fromName
      };
      // Nur senden, wenn ein neues Passwort eingegeben wurde — sonst lassen
      if (newPassword) body.password = newPassword;
      const res = await tenantFetch(`/api/tenants/${activeTenant._id}/email-config`, {
        method: 'PATCH',
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Fehler');
      setCfg(data.emailConfig);
      setNewPassword('');
      setSuccess('Gespeichert.');
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const clearPassword = async () => {
    if (!window.confirm('Gespeichertes Passwort wirklich löschen?')) return;
    setSaving(true);
    try {
      const res = await tenantFetch(`/api/tenants/${activeTenant._id}/email-config`, {
        method: 'PATCH',
        body: JSON.stringify({ clearPassword: true })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Fehler');
      setCfg(data.emailConfig);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const sendTestMail = async () => {
    setTesting(true);
    setTestResult('');
    try {
      const res = await tenantFetch(`/api/tenants/${activeTenant._id}/email-config/test`, {
        method: 'POST',
        body: JSON.stringify({ to: testTo || cfg.fromAddress })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Fehler');
      setTestResult(`✓ Mail abgeschickt an ${(data.accepted || []).join(', ') || testTo || cfg.fromAddress} (id: ${data.messageId || '–'})`);
    } catch (err) {
      setTestResult(`✗ ${err.message}`);
    } finally {
      setTesting(false);
    }
  };

  if (loading) return <p>Lädt…</p>;

  return (
    <div className="email-config-panel">
      <form onSubmit={save}>
        <div className="data-source-row">
          <label className="data-source-field">
            <span>SMTP-Host</span>
            <input
              className="text-input"
              placeholder="smtp.example.com"
              value={cfg.host}
              onChange={e => setCfg(c => ({ ...c, host: e.target.value }))}
              disabled={!canEdit}
            />
          </label>
          <label className="data-source-field small">
            <span>Port</span>
            <input
              type="number"
              className="text-input"
              value={cfg.port}
              onChange={e => setCfg(c => ({ ...c, port: e.target.value }))}
              disabled={!canEdit}
            />
          </label>
          <label className="data-source-field small" style={{ alignSelf: 'flex-end', flexBasis: 'auto' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input
                type="checkbox"
                checked={!!cfg.secure}
                onChange={e => setCfg(c => ({ ...c, secure: e.target.checked }))}
                disabled={!canEdit}
              />
              SSL/TLS direkt (port 465)
            </span>
          </label>
        </div>

        <div className="data-source-row">
          <label className="data-source-field">
            <span>Benutzername (login)</span>
            <input
              className="text-input"
              placeholder="hello@example.com"
              value={cfg.user}
              onChange={e => setCfg(c => ({ ...c, user: e.target.value }))}
              disabled={!canEdit}
            />
          </label>
          <label className="data-source-field">
            <span>Passwort {cfg.passwordSet ? '(neu eingeben um zu ändern)' : ''}</span>
            <input
              type="password"
              className="text-input"
              placeholder={cfg.passwordSet ? cfg.passwordHint || 'gespeichert' : ''}
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              disabled={!canEdit}
            />
          </label>
        </div>

        <div className="data-source-row">
          <label className="data-source-field">
            <span>Absender-Adresse (from)</span>
            <input
              className="text-input"
              placeholder="noreply@example.com"
              value={cfg.fromAddress}
              onChange={e => setCfg(c => ({ ...c, fromAddress: e.target.value }))}
              disabled={!canEdit}
            />
          </label>
          <label className="data-source-field">
            <span>Absender-Name (optional)</span>
            <input
              className="text-input"
              placeholder="Acme GmbH"
              value={cfg.fromName}
              onChange={e => setCfg(c => ({ ...c, fromName: e.target.value }))}
              disabled={!canEdit}
            />
          </label>
        </div>

        {error && <div className="form-error">{error}</div>}
        {success && <div className="success-message small">{success}</div>}

        {canEdit && (
          <div className="email-config-actions">
            <button type="submit" className="primary-btn" disabled={saving}>
              {saving ? '…' : 'Speichern'}
            </button>
            {cfg.passwordSet && (
              <button type="button" className="link-btn danger" onClick={clearPassword} disabled={saving}>
                Gespeichertes Passwort löschen
              </button>
            )}
          </div>
        )}
      </form>

      {canEdit && cfg.host && (
        <div className="email-config-test">
          <h4>Test-Mail senden</h4>
          <p className="section-hint">
            Schickt eine kurze Test-Nachricht über die obigen Einstellungen — bestätigt, dass SMTP funktioniert,
            bevor ein echter Workflow abgeschickt wird.
          </p>
          <div className="data-source-row">
            <label className="data-source-field">
              <span>Empfänger (leer = an Absender-Adresse)</span>
              <input
                className="text-input"
                placeholder={cfg.fromAddress}
                value={testTo}
                onChange={e => setTestTo(e.target.value)}
              />
            </label>
            <button
              type="button"
              className="btn-sm-primary"
              style={{ alignSelf: 'flex-end' }}
              onClick={sendTestMail}
              disabled={testing}
            >
              {testing ? 'Sende…' : 'Test senden'}
            </button>
          </div>
          {testResult && <div className="section-hint" style={{ marginTop: 8 }}>{testResult}</div>}
        </div>
      )}
    </div>
  );
}
